import React, { useCallback, useEffect, useState } from "react";
import { FiDownload, FiFileText, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import {
  expenseReportsApi,
  ExpenseReport,
  ExpenseLineInput,
} from "../api/expenseReports";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";

const CATEGORY_OPTIONS = [
  { value: "transport", label: "Transport" },
  { value: "repas", label: "Repas" },
  { value: "hebergement", label: "Hébergement" },
  { value: "kilometrique", label: "Indemnités kilométriques" },
  { value: "fournitures", label: "Fournitures" },
  { value: "autre", label: "Autre" },
];

const STATUS_VARIANT: Record<
  ExpenseReport["status"],
  "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

interface LineForm extends ExpenseLineInput {
  receipt?: File | null;
}

const emptyLine = (): LineForm => ({
  category: "transport",
  description: "",
  amount: 0,
  expense_date: new Date().toISOString().slice(0, 10),
  receipt: null,
});

const NotesDeFraisPage: React.FC = () => {
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [comment, setComment] = useState("");
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await expenseReportsApi.listMine();
      setReports(res.data.data?.reports ?? []);
    } catch {
      toast.error("Impossible de charger vos notes de frais");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateLine = (index: number, patch: Partial<LineForm>) => {
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    );
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setComment("");
    setLines([emptyLine()]);
  };

  const handleSubmit = async () => {
    const validLines = lines.filter(
      (l) => l.description.trim() && l.amount > 0,
    );
    if (validLines.length === 0) {
      toast.error("Ajoutez au moins une dépense valide");
      return;
    }

    setSubmitting(true);
    try {
      const hasReceipts = validLines.some((l) => l.receipt);
      if (hasReceipts) {
        const formData = new FormData();
        formData.append(
          "lines",
          JSON.stringify(
            validLines.map(
              ({ category, description, amount, expense_date }) => ({
                category,
                description: description.trim(),
                amount,
                expense_date,
              }),
            ),
          ),
        );
        if (comment.trim()) formData.append("comment", comment.trim());
        validLines.forEach((line, index) => {
          if (line.receipt) formData.append(`receipt_${index}`, line.receipt);
        });
        await expenseReportsApi.createWithReceipts(formData);
      } else {
        await expenseReportsApi.create({
          comment: comment.trim() || undefined,
          lines: validLines.map(
            ({ category, description, amount, expense_date }) => ({
              category,
              description: description.trim(),
              amount,
              expense_date,
            }),
          ),
        });
      }
      toast.success("Note de frais envoyée au service RH");
      setShowForm(false);
      resetForm();
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec de l'envoi";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (report: ExpenseReport) => {
    if (!window.confirm("Annuler cette note de frais ?")) return;
    try {
      await expenseReportsApi.cancel(report.id);
      toast.success("Note annulée");
      await loadData();
    } catch {
      toast.error("Impossible d'annuler la note");
    }
  };

  const handleDownloadReceipt = async (
    reportId: number,
    lineId: number,
    filename?: string | null,
  ) => {
    try {
      const res = await expenseReportsApi.downloadReceipt(reportId, lineId);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "justificatif";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const totalPending = reports
    .filter((r) => r.status === "pending")
    .reduce((sum, r) => sum + r.total_amount, 0);

  if (loading) {
    return (
      <div
        style={{ padding: "3rem", display: "flex", justifyContent: "center" }}
      >
        <LoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: 960, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <h1
            style={{
              margin: "0 0 0.35rem",
              fontSize: "var(--font-size-2xl)",
              fontWeight: 700,
              color: "var(--color-primary)",
            }}
          >
            Notes de frais
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
            }}
          >
            Déclarez vos dépenses professionnelles
          </p>
        </div>
        <Button icon={<FiPlus size={16} />} onClick={() => setShowForm(true)}>
          Nouvelle note de frais
        </Button>
      </div>

      {totalPending > 0 && (
        <Card
          padding="1.25rem"
          style={{
            marginBottom: "1.5rem",
            background: "linear-gradient(135deg, #ECFDF5 0%, #F0FDF4 100%)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: "var(--font-size-sm)",
                color: "var(--color-text-secondary)",
              }}
            >
              Montant en attente de validation
            </p>
            <p
              style={{
                margin: "0.15rem 0 0",
                fontSize: "var(--font-size-xl)",
                fontWeight: 700,
                color: "#059669",
              }}
            >
              {totalPending.toLocaleString("fr-FR", {
                style: "currency",
                currency: "EUR",
              })}
            </p>
          </div>
        </Card>
      )}

      <h2
        style={{
          margin: "0 0 1rem",
          fontSize: "var(--font-size-base)",
          fontWeight: 600,
        }}
      >
        Vos notes de frais
      </h2>
      {reports.length === 0 ? (
        <Card padding="2rem">
          <EmptyState
            title="Aucune note de frais"
            description="Créez une note, ajoutez vos dépenses et joignez vos justificatifs."
            actionLabel="Nouvelle note de frais"
            onAction={() => setShowForm(true)}
          />
        </Card>
      ) : (
        <Card padding="0">
          {reports.map((report, index) => (
            <div
              key={report.id}
              style={{
                borderTop:
                  index > 0 ? "1px solid var(--color-border-light)" : undefined,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "1rem 1.25rem",
                  cursor: "pointer",
                }}
                onClick={() =>
                  setExpandedId(expandedId === report.id ? null : report.id)
                }
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "var(--radius-lg)",
                    background: "rgba(16, 185, 129, 0.12)",
                    color: "#10B981",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FiFileText size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 500,
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    {report.title}
                  </p>
                  <p
                    style={{
                      margin: "0.15rem 0 0",
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    {report.lines_count} dépense
                    {report.lines_count > 1 ? "s" : ""}
                    {" · "}
                    {format(new Date(report.created_at), "d MMM yyyy", {
                      locale: fr,
                    })}
                  </p>
                </div>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  {report.total_amount.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                  })}
                </p>
                <Badge variant={STATUS_VARIANT[report.status]} size="sm">
                  {report.status_label}
                </Badge>
                {report.status === "pending" && (
                  <button
                    type="button"
                    title="Annuler"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel(report);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                    }}
                  >
                    <FiX size={18} />
                  </button>
                )}
              </div>
              {expandedId === report.id && report.lines && (
                <div style={{ padding: "0 1.25rem 1rem 4.5rem" }}>
                  {report.lines.map((line) => (
                    <div
                      key={line.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0.5rem 0",
                        fontSize: "var(--font-size-sm)",
                        borderTop: "1px solid var(--color-border-light)",
                        gap: "0.75rem",
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 500 }}>
                          {line.category_label}
                        </span>
                        {" - "}
                        {line.description}
                        <span
                          style={{
                            color: "var(--color-text-muted)",
                            marginLeft: "0.35rem",
                          }}
                        >
                          ({format(new Date(line.expense_date), "dd/MM/yyyy")})
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          {line.amount.toLocaleString("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                          })}
                        </span>
                        {line.has_receipt && (
                          <button
                            type="button"
                            title="Télécharger le justificatif"
                            onClick={() =>
                              handleDownloadReceipt(
                                report.id,
                                line.id,
                                line.receipt_filename,
                              )
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: "var(--color-brand)",
                              cursor: "pointer",
                            }}
                          >
                            <FiDownload size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {report.rh_comment && report.status === "rejected" && (
                    <p
                      style={{
                        margin: "0.5rem 0 0",
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-danger)",
                      }}
                    >
                      Motif RH : {report.rh_comment}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          resetForm();
        }}
        title="Nouvelle note de frais"
        size="lg"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Envoi…" : "Envoyer à la RH"}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {lines.map((line, index) => (
            <div
              key={index}
              style={{
                padding: "1rem",
                border: "1px solid var(--color-border-light)",
                borderRadius: "var(--radius-lg)",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}
                >
                  Dépense {index + 1}
                </span>
                {lines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLine(index)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                    }}
                  >
                    <FiTrash2 size={16} />
                  </button>
                )}
              </div>
              <Select
                label="Catégorie"
                value={line.category}
                onChange={(e) =>
                  updateLine(index, { category: e.target.value })
                }
                options={CATEGORY_OPTIONS}
              />
              <Input
                label="Description"
                value={line.description}
                onChange={(e) =>
                  updateLine(index, { description: e.target.value })
                }
                placeholder="ex. Taxi client Lyon"
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.75rem",
                }}
              >
                <Input
                  label="Montant (€)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.amount || ""}
                  onChange={(e) =>
                    updateLine(index, {
                      amount: parseFloat(e.target.value) || 0,
                    })
                  }
                />
                <Input
                  label="Date"
                  type="date"
                  value={line.expense_date}
                  onChange={(e) =>
                    updateLine(index, { expense_date: e.target.value })
                  }
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.35rem",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 500,
                  }}
                >
                  Justificatif (optionnel)
                </label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) =>
                    updateLine(index, { receipt: e.target.files?.[0] ?? null })
                  }
                  style={{ fontSize: "var(--font-size-sm)" }}
                />
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            icon={<FiPlus size={14} />}
            onClick={addLine}
          >
            Ajouter une dépense
          </Button>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.35rem",
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
              }}
            >
              Commentaire (optionnel)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                fontSize: "var(--font-size-sm)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                fontFamily: "var(--font-family)",
                resize: "vertical",
              }}
              placeholder="Précisions pour le service RH…"
            />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-xs)",
              color: "var(--color-text-muted)",
            }}
          >
            Total :{" "}
            <strong>
              {lines
                .reduce((sum, l) => sum + (l.amount > 0 ? l.amount : 0), 0)
                .toLocaleString("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                })}
            </strong>
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default NotesDeFraisPage;
